/**
 * DataExplorerTab — Interactive SQL query bar + sortable/paginated results table.
 *
 * Replaces the old fixed 50-row Data tab with a full explorer:
 * - Monaco SQL editor (3-line default, expandable)
 * - Run button + Cmd/Ctrl+Enter shortcut
 * - Sortable columns with server-side sorting
 * - Server-side pagination (50/100/200 rows per page)
 * - Column header click for column profile popover
 * - Before/After toggle for input vs output table
 * - Export CSV button
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  Download as DownloadIcon,
  UnfoldMore as ExpandIcon,
  UnfoldLess as CollapseIcon,
  ArrowDropDown as ChevronIcon,
  Abc as TextTypeIcon,
  Numbers as NumericTypeIcon,
  CalendarMonth as DateTypeIcon,
  CheckBox as BoolTypeIcon,
  FactCheck as FactCheckIcon,
  CheckCircleOutline as PassIcon,
  ErrorOutline as FailErrorIcon,
  WarningAmber as FailWarningIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import Editor from '@monaco-editor/react';
import { useWorkspaceQuery, useWorkspaceColumnProfile } from '../../hooks/useWorkspaceSession';
import { getApiErrorMessage } from '../../services/apiErrors';
import type { WorkspaceTable } from '../../services/workspaceApi';
import TransformBar from './TransformBar';
import SaveTransformBar from './SaveTransformBar';
import ColumnContextMenu from './ColumnContextMenu';
import type { ColumnAction } from './ColumnContextMenu';
import ColumnProfilePanel from './ColumnProfilePanel';
import {
  type TransformState,
  initStateFromTable,
  buildSqlFromState,
  buildUnionSchemaHints,
  isStateModified,
  applyFilter,
  applySort,
  applyColumnFunction,
  applyCoalesce,
  applyRename,
  applyDropColumn,
  applyCast,
  applySplitColumn,
  applyExtractPattern,
  applyMoveColumn,
  quoteIdent,
  inferSelectColumnOutputType,
  selectColumnExpressionHasDateTimeStrftime,
} from './sqlBuilder';
import { useGridSelection, type SelectionDescriptor } from './useGridSelection';
// normalizeTableKey removed — use table names from workspace API as-is
import { WORKSPACE_MONACO_EDITOR_DEFAULTS } from './workspaceMonacoDefaults';
import { useMonacoTableCompletion } from './hooks/useMonacoTableCompletion';
import {
  buildValidationSql,
  buildViolationRowsSql,
  extractColumnRules,
  filterColumnRulesToView,
  type ValidationSummary,
  type SchemaRule,
} from './buildValidationSql';
import { queryWorkspace, downloadWorkspaceTable } from '../../services/workspaceApi';
import { evaluateCellViolations, severityRank, type CellViolation } from './evaluateClientRules';
import { useNotifications } from '../../context/NotificationContext';

// Monaco `sql` has no diagnostics; flag probable reserved-word typos (edit distance === 1 only).
// Curated ANSI / PostgreSQL–style + common DuckDB tokens (deduped). Checked: SELEC→SELECT,
// FRO→FROM (min token length 3), WHRE→WHERE, ORDE→ORDER, JOIM→JOIN.
const RESERVED_SQL_WORDS_LIST: string[] = (() => {
  const raw =
    'ALL ALTER ANALYZE AND ANY ARRAY AS ASC BETWEEN BOTH BY CASE CAST CHECK COLLATE COLUMN CONSTRAINT '
      + 'CREATE CROSS CURRENT DAY DECLARE DEFAULT DELETE DESC DISTINCT DROP ELSE END ENUM ESCAPE EXCEPT EXECUTE EXISTS '
      + 'EXPLAIN FALSE FETCH FILTER FIRST FOLLOWING FOR FOREIGN FROM FULL GENERATED GRANT GROUP GROUPING HAVING '
      + 'HOUR IF ILIKE IN INDEX INNER INOUT INSERT INTERSECT INTERVAL INTO IS JOIN KEYS LATERAL LEADING LEFT '
      + 'LIKE LIMIT MACRO MAP MATCH MATERIALIZED MEDIAN MERGE MINUTE MONTH NATURAL NEXT NOT NULL OFFSET ON ONLY OPEN '
      + 'OR ORDER ORDINALITY OUTER OVER PARTITION PRECEDING PRIMARY QUALIFY RANGE READ RECURSIVE REFERENCES '
      + 'REPLACE RETURN RETURNING REVOKE RIGHT ROLE ROW ROWS SAMPLE SCHEMA SECOND SELECT SESSION SET SKIP SOME '
      + 'STRUCTURED SYMMETRIC SYSTEM TABLE TABLESAMPLE TEMP THEN TIES TIMESTAMP TO TOP TRAILING TRANSACTION '
      + 'TRUE TRUNCATE UNBOUNDED UNION UNIQUE UNKNOWN UNMATCHED UPDATE USER USING UUID VALUES VIEW VIRTUAL WEEK WHEN '
      + 'WHERE WINDOW WITH YEAR ZONE '
      + 'AVG COALESCE COUNT GREATEST LEAST MAX MIN SUM '
      + 'BEGIN COMMIT DO PIVOT PLACING POSITION PROCEDURE SEQUENCE TEMPORARY TRIGGER UNPIVOT VOLATILE';

  const parts = raw.trim().split(/\s+/);
  const seen = new Set<string>();
  const out: string[] = [];
  for (let pi = 0; pi < parts.length; pi++) {
    const w = parts[pi];
    const u = w.toUpperCase();
    if (!u || seen.has(u)) continue;
    seen.add(u);
    out.push(u);
  }
  return out;
})();

const RESERVED_SQL_WORDS = new Set(RESERVED_SQL_WORDS_LIST);

function forEachBareSqlWord(sql: string, fn: (w: string, from: number, to: number) => void): void {
  let i = 0;
  while (i < sql.length) {
    const c = sql.charCodeAt(i);
    const n = sql.charCodeAt(i + 1);
    if (c === 45 && n === 45) {
      while (i < sql.length && sql.charCodeAt(i) !== 10 && sql.charCodeAt(i) !== 13) i++;
      continue;
    }
    if (c === 47 && n === 42) {
      i += 2;
      while (i < sql.length - 1 && !(sql.charCodeAt(i) === 42 && sql.charCodeAt(i + 1) === 47)) i++;
      i += 2;
      continue;
    }
    if (c === 39) {
      i++;
      while (i < sql.length) {
        const ch = sql.charCodeAt(i);
        if (ch === 39 && sql.charCodeAt(i + 1) === 39) i += 2;
        else if (ch === 39) {
          i++;
          break;
        } else i++;
      }
      continue;
    }
    if (c === 34) {
      i++;
      while (i < sql.length) {
        const ch = sql.charCodeAt(i);
        if (ch === 34 && sql.charCodeAt(i + 1) === 34) i += 2;
        else if (ch === 34) {
          i++;
          break;
        } else i++;
      }
      continue;
    }
    /* Backtick-quoted identifiers (DuckDB / MySQL-style): do not typo-scan inside */
    if (c === 96) {
      i++;
      while (i < sql.length && sql.charCodeAt(i) !== 96) i++;
      if (i < sql.length) i++;
      continue;
    }
    /* Bracket identifiers [dbo].[name] — skip interior */
    if (c === 91) {
      i++;
      while (i < sql.length && sql.charCodeAt(i) !== 93) i++;
      if (i < sql.length) i++;
      continue;
    }
    if ((c >= 65 && c <= 90) || (c >= 97 && c <= 122) || c === 95) {
      const st = i;
      i++;
      while (i < sql.length) {
        const ch = sql.charCodeAt(i);
        if ((ch >= 65 && ch <= 90) || (ch >= 97 && ch <= 122) || (ch >= 48 && ch <= 57) || ch === 95) i++;
        else break;
      }
      // Include 3-letter tokens so typos like `FRO` → FROM are flagged (4+ would skip them).
      if (i - st >= 3) fn(sql.slice(st, i), st, i);
      continue;
    }
    i++;
  }
}

function isExactlyOneEdit(a: string, b: string): boolean {
  if (a === b || Math.abs(a.length - b.length) > 1) return false;
  if (a.length === b.length) {
    let diff = 0;
    for (let k = 0; k < a.length; k++) {
      if (a.charCodeAt(k) !== b.charCodeAt(k)) {
        diff++;
        if (diff > 1) return false;
      }
    }
    return diff === 1;
  }
  const short = a.length < b.length ? a : b;
  const long = a.length < b.length ? b : a;
  let k = 0;
  while (k < short.length && short.charCodeAt(k) === long.charCodeAt(k)) k++;
  return short.slice(k) === long.slice(k + 1);
}

function sqlTypoKeywordSuggestion(word: string): string | null {
  const u = word.toUpperCase();
  if (RESERVED_SQL_WORDS.has(u)) return null;
  let bestKeyword: string | null = null;
  let bestLen = -1;
  for (let i = 0; i < RESERVED_SQL_WORDS_LIST.length; i++) {
    const kw = RESERVED_SQL_WORDS_LIST[i];
    if (Math.abs(u.length - kw.length) > 1) continue;
    if (!isExactlyOneEdit(u, kw)) continue;
    if (kw.length > bestLen) {
      bestLen = kw.length;
      bestKeyword = kw;
    }
  }
  return bestKeyword;
}

function offsetToMonacoPos(text: string, offset: number): { line: number; col: number } {
  let line = 1;
  let lastNl = -1;
  for (let p = 0; p < offset; p++) {
    if (text.charCodeAt(p) === 10) {
      line++;
      lastNl = p;
    }
  }
  return { line, col: offset - lastNl };
}

const SQL_TYPO_MARKER_OWNER = 'data-explorer-sql-typo';

// ── Types ────────────────────────────────────────────────────────────

/** Per-column quality metrics for quality bar rendering. */
export interface ColumnQualityInfo {
  nullPct: number;
  validPct: number;
  violationCount: number;
}

/** A single health issue from the mapping health API. */
export interface HealthIssue {
  column?: string;
  message: string;
  severity: 'error' | 'warning' | 'info';
  remediation?: string;
  rowIndices?: number[];
}

interface DataExplorerTabProps {
  sessionId: string;
  tableName: string | null;
  inputTableName?: string | null;
  columnDiff?: { added: string[]; removed: string[] } | null;
  isSourceNode?: boolean;
  /** Operation context for "Update Transform" flow. null = exploration mode. */
  operationContext?: {
    operationId: string;
    operationType: string;
    config: Record<string, any>;
  } | null;
  /** Available workspace tables (for Join and column type info). */
  workspaceTables?: WorkspaceTable[];
  /** Increment to trigger a data refresh (e.g. after Code tab save). */
  dataRefreshKey?: number;
  /** Callback when a new operation is created via "Save as Transform". */
  onOperationCreated?: (operationId: string) => void;
  /** Callback when an existing operation is updated via "Update Transform". */
  onOperationUpdated?: () => void;
  /** Callback to send a scoped message to the agent about a specific column. */
  onAskAboutColumn?: (tableName: string, columnName: string) => void;
  /** Error message to display when the table cannot be queried (e.g. failed mapping). */
  errorMessage?: string | null;
  /** Optional health issues for cell-level violation highlighting. */
  healthIssues?: HealthIssue[];
  /** Optional per-column quality metrics for column header quality bars. */
  columnQuality?: Record<string, ColumnQualityInfo>;
  /** Target schema snapshot for full-data validation. */
  targetSchemaSnapshot?: Record<string, any>;
  /** Fires when cell/column/row selection changes. */
  onSelectionChange?: (selection: SelectionDescriptor | null) => void;
}

type SortDirection = 'asc' | 'desc';

// ── Helpers ──────────────────────────────────────────────────────────

function formatElapsed(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = (ms / 1000).toFixed(1);
  return `${s}s`;
}

function parseQuotedIdent(identExpr: string): string | null {
  const match = identExpr.match(/^"((?:[^"]|"")*)"$/);
  if (!match) return null;
  return match[1].replace(/""/g, '"');
}

function formatCellDisplayValue(value: unknown, dtype?: string, selectExpression?: string): string {
  const str = String(value);
  // DATE columns: strip midnight time DuckDB appends, unless user chose a datetime display format.
  if (
    /^date$/i.test(dtype || '')
    && !selectColumnExpressionHasDateTimeStrftime(selectExpression || '')
    && /^\d{4}-\d{2}-\d{2} 00:00:00$/.test(str)
  ) {
    return str.slice(0, 10);
  }
  return str;
}

// ── Column Type Icon Helper ──────────────────────────────────────────

function getTypeIcon(dtype: string): React.ReactNode {
  if (/varchar|text|string|char/i.test(dtype)) {
    return <TextTypeIcon sx={{ fontSize: 12, color: '#6366f1', opacity: 0.7 }} />;
  }
  if (/int|bigint|double|float|decimal|numeric/i.test(dtype)) {
    return <NumericTypeIcon sx={{ fontSize: 12, color: '#0891b2', opacity: 0.7 }} />;
  }
  if (/date|timestamp|datetime/i.test(dtype)) {
    return <DateTypeIcon sx={{ fontSize: 12, color: '#d97706', opacity: 0.7 }} />;
  }
  if (/bool/i.test(dtype)) {
    return <BoolTypeIcon sx={{ fontSize: 12, color: '#059669', opacity: 0.7 }} />;
  }
  return null;
}

// ── SortableHeaderCell ───────────────────────────────────────────────

interface SortableHeaderCellProps {
  id: string;
  onMouseDown?: React.MouseEventHandler<HTMLTableCellElement>;
  onMouseEnter?: () => void;
  sx?: object;
  children: React.ReactNode;
}

/**
 * A TableCell wrapper that makes each column header draggable for reordering.
 * Drag listeners are scoped to a dedicated handle icon so sort/select clicks
 * on the rest of the cell continue to work as before.
 */
const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  id, onMouseDown, onMouseEnter, sx, children,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <TableCell
      ref={setNodeRef}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      {...attributes}
      sx={{
        ...sx,
        transform: transform ? `translateX(${Math.round(transform.x)}px)` : undefined,
        transition,
        opacity: isDragging ? 0.4 : 1,
        // Show grab cursor only on the drag handle area
        '&:hover .col-drag-handle': { opacity: 0.5 },
      }}
    >
      {/* Drag handle — absolutely positioned so it takes zero layout space.
          Listeners scoped here so sort/shift-select clicks still work. */}
       <Box
        component="span"
        className="col-drag-handle"
        {...listeners}
        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          left: 2,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: 'text.disabled',
          opacity: 0,
          zIndex: 1,
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 11 }} />
      </Box>
      {children}
    </TableCell>
  );
};

// ── Main Component ───────────────────────────────────────────────────

const DataExplorerTab: React.FC<DataExplorerTabProps> = ({
  sessionId,
  tableName,
  inputTableName,
  columnDiff,
  isSourceNode,
  operationContext,
  workspaceTables = [],
  dataRefreshKey,
  onOperationCreated,
  onOperationUpdated,
  onAskAboutColumn,
  errorMessage,
  healthIssues,
  columnQuality,
  targetSchemaSnapshot,
  onSelectionChange,
}) => {
  const { showToast } = useNotifications();
  const [previewTab, setPreviewTab] = useState<'after' | 'before'>('after');
  const [sql, setSql] = useState('');
  const [editorExpanded, setEditorExpanded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [lastSuccessfulSql, setLastSuccessfulSql] = useState('');
  const [columnNullStats, setColumnNullStats] = useState<Record<string, { nullCount: number; total: number; pct: number }>>({});
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [queryTime, setQueryTime] = useState<number | null>(null);
  const [totalRowCount, setTotalRowCount] = useState<number | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileColumn, setProfileColumn] = useState<string>('');

  // ── ColumnContextMenu state ─────────────────────────────────────
  const [contextMenuAnchor, setContextMenuAnchor] = useState<HTMLElement | null>(null);
  const [contextMenuColumn, setContextMenuColumn] = useState<string>('');
  const [contextMenuCellValue, setContextMenuCellValue] = useState<string | undefined>(undefined);

  // ── Rename dialog state ───────────────────────────────────────
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameColumn, setRenameColumn] = useState('');
  const [renameNewName, setRenameNewName] = useState('');

  // ── Split dialog state ──────────────────────────────────────
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitDialogColumn, setSplitDialogColumn] = useState('');
  const [splitDialogDelimiter, setSplitDialogDelimiter] = useState(',');
  const [splitDialogCount, setSplitDialogCount] = useState(2);

  // ── Extract Pattern dialog state ────────────────────────────────
  const [extractPatternOpen, setExtractPatternOpen] = useState(false);
  const [extractPatternColumn, setExtractPatternColumn] = useState('');
  const [extractPatternRegex, setExtractPatternRegex] = useState('');
  const [extractPatternGroupIndex, setExtractPatternGroupIndex] = useState<number | ''>('');
  const [extractPatternNewCol, setExtractPatternNewCol] = useState('');

  // ── Move Column dialog state (for "before" / "after") ──────────
  const [moveColumnDialogOpen, setMoveColumnDialogOpen] = useState(false);
  const [moveColumnSource, setMoveColumnSource] = useState('');
  const [moveColumnPosition, setMoveColumnPosition] = useState<'before' | 'after'>('before');
  const [fillNullsRequest, setFillNullsRequest] = useState<string | null>(null);

  // ── TransformBar State ─────────────────────────────────────────
  // Keyed by activeTableName to preserve separate state per table (§M4)
  const [transformStates, setTransformStates] = useState<Record<string, TransformState>>({});
  const [stateHistoryByTable, setStateHistoryByTable] = useState<Record<string, TransformState[]>>({});
  const [redoHistoryByTable, setRedoHistoryByTable] = useState<Record<string, TransformState[]>>({});
  const [isManuallyEdited, setIsManuallyEdited] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Ignore the next Monaco onChange fired by programmatic setSql updates. */
  const suppressSqlManualEditRef = useRef(false);

  // ── Full Validation State ─────────────────────────────────────────
  const [validationResult, setValidationResult] = useState<ValidationSummary[] | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationHealthIssues, setValidationHealthIssues] = useState<HealthIssue[] | undefined>(undefined);

  const queryMutation = useWorkspaceQuery(sessionId);
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const [monacoInstance, setMonacoInstance] = useState<any>(null);
  const [editorInstance, setEditorInstance] = useState<any>(null);
  const result = queryMutation.data;

  const activeTableName = previewTab === 'before' ? inputTableName : tableName;

  // Current TransformState for the active table
  const transformState = useMemo(() => {
    if (!activeTableName) return null;
    return transformStates[activeTableName] || null;
  }, [transformStates, activeTableName]);

  const stateHistory = useMemo(
    () => (activeTableName ? stateHistoryByTable[activeTableName] ?? [] : []),
    [activeTableName, stateHistoryByTable],
  );
  const redoHistory = useMemo(
    () => (activeTableName ? redoHistoryByTable[activeTableName] ?? [] : []),
    [activeTableName, redoHistoryByTable],
  );

  const setSqlProgrammatically = useCallback((newSql: string) => {
    suppressSqlManualEditRef.current = true;
    setSql(newSql);
  }, []);

  // Column types for the visible result set. Prefer active transform-derived
  // types (cast/rename) over the base table schema so header icons/context
  // menus reflect the transformed output, not just the source table.
  const columnTypes = useMemo(() => {
    if (!activeTableName) return [];
    const wt = workspaceTables.find((t) => t.name === activeTableName);
    const baseColumns = wt?.columns || [];
    const columnTypeMap = new Map(baseColumns.map((column) => [column.name, column.type]));

    for (const selectColumn of transformState?.selectColumns || []) {
      if (selectColumn.isRename) {
        const sourceName = parseQuotedIdent(selectColumn.expression);
        const sourceType = sourceName ? columnTypeMap.get(sourceName) : null;
        if (sourceType) {
          columnTypeMap.set(selectColumn.alias, sourceType);
        }
        continue;
      }

      const castType = inferSelectColumnOutputType(selectColumn);
      if (castType && selectColumn.alias) {
        columnTypeMap.set(selectColumn.alias, castType);
      }
    }

    const visibleColumns = result?.columns || baseColumns.map((column) => column.name);
    return visibleColumns.map((columnName) => ({
      name: columnName,
      type: columnTypeMap.get(columnName) || 'VARCHAR',
    }));
  }, [workspaceTables, activeTableName, transformState, result?.columns]);
  const columnTypeByName = useMemo(
    () => new Map(columnTypes.map((column) => [column.name, column.type])),
    [columnTypes],
  );
  const selectExpressionByColumn = useMemo(() => {
    const map = new Map<string, string>();
    for (const selectColumn of transformState?.selectColumns ?? []) {
      if (selectColumn.alias) {
        map.set(selectColumn.alias, selectColumn.expression);
      }
    }
    return map;
  }, [transformState?.selectColumns]);

  const viewColumnNames = useMemo(() => {
    if (!activeTableName) return [] as string[];
    const wt = workspaceTables.find((t) => t.name === activeTableName);
    return wt?.columns?.map((c) => c.name) ?? result?.columns ?? [];
  }, [workspaceTables, activeTableName, result?.columns]);

  const resolveBaseColumnsForState = useCallback((state: TransformState): string[] => {
    if (state.baseTable === activeTableName && result?.columns?.length) {
      return result.columns;
    }
    const table = workspaceTables.find((t) => t.name === state.baseTable);
    return table?.columns?.map((column) => column.name) ?? [];
  }, [activeTableName, result?.columns, workspaceTables]);

  const buildSqlForTransformState = useCallback((state: TransformState): string => {
    if (state.unions.length === 0) {
      return buildSqlFromState(state);
    }
    return buildSqlFromState(state, {
      unionHints: buildUnionSchemaHints(
        state,
        resolveBaseColumnsForState(state),
        workspaceTables,
      ),
    });
  }, [resolveBaseColumnsForState, workspaceTables]);

  const mappedColumnRules = useMemo(() => {
    if (!targetSchemaSnapshot || !activeTableName) return [];
    return filterColumnRulesToView(
      extractColumnRules(targetSchemaSnapshot, activeTableName),
      viewColumnNames,
    );
  }, [targetSchemaSnapshot, activeTableName, viewColumnNames]);

  const isTableMissing = useMemo(() => {
    if (!activeTableName || workspaceTables.length === 0) return false;
    return !workspaceTables.some((t) => t.name === activeTableName);
  }, [activeTableName, workspaceTables]);

  useEffect(() => {
    let cancelled = false;

    if (!sessionId || !lastSuccessfulSql.trim() || !result?.columns?.length) {
      setColumnNullStats({});
      return () => { cancelled = true; };
    }

    const cleanSql = lastSuccessfulSql.trim().replace(/;$/, '');
    const aggregateColumns = result.columns.map((columnName, index) => ({
      columnName,
      alias: `__null_count_${index}`,
    }));
    const nullStatsSql = [
      'SELECT',
      'COUNT(*) AS "__row_count"',
      ...aggregateColumns.map(
        ({ columnName, alias }) =>
          `, SUM(CASE WHEN ${quoteIdent(columnName)} IS NULL THEN 1 ELSE 0 END) AS ${quoteIdent(alias)}`,
      ),
      `FROM (${cleanSql}) _null_stats`,
    ].join(' ');

    setColumnNullStats({});
    queryWorkspace(sessionId, nullStatsSql, 1, 0, false)
      .then((data) => {
        if (cancelled) return;
        const row = data.rows?.[0] ?? {};
        const total = Number(row.__row_count) || 0;
        const nextStats = Object.fromEntries(
          aggregateColumns.map(({ columnName, alias }) => {
            const nullCount = Number(row[alias]) || 0;
            return [columnName, { nullCount, total, pct: total > 0 ? nullCount / total : 0 }];
          }),
        );
        setColumnNullStats(nextStats);
      })
      .catch(() => {
        if (!cancelled) setColumnNullStats({});
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId, lastSuccessfulSql, result?.columns]);

  const baseColumnNames = useMemo(() => {
    if (!activeTableName) return new Set<string>();
    const wt = workspaceTables.find((t) => t.name === activeTableName);
    return new Set(wt?.columns?.map((c) => c.name) ?? []);
  }, [workspaceTables, activeTableName]);

  // Saved transforms materialize new columns onto the output table (table API works).
  // Preview-only columns exist only in the query — profile via SQL subquery.
  const resolveProfileQuerySql = useCallback((column: string): string | undefined => {
    if (!column || previewTab !== 'after' || !lastSuccessfulSql.trim()) return undefined;
    if (baseColumnNames.has(column)) return undefined;
    return lastSuccessfulSql;
  }, [previewTab, lastSuccessfulSql, baseColumnNames]);

  // Lazy-load column profile for the context menu column (for smart suggestions)
  const { data: contextMenuProfile, isLoading: contextMenuProfileLoading } = useWorkspaceColumnProfile(
    sessionId,
    activeTableName ?? null,
    contextMenuAnchor ? contextMenuColumn : null, // only fetch when menu is open
    contextMenuAnchor ? resolveProfileQuerySql(contextMenuColumn) : null,
  );


  // ── TransformBar handlers ──────────────────────────────────────
  const handleTransformStateChange = useCallback((newState: TransformState) => {
    if (!activeTableName) return;
    // Push current state to undo history, clear redo stack
    const currentState = transformStates[activeTableName];
    if (currentState) {
      setStateHistoryByTable((prev) => ({
        ...prev,
        [activeTableName]: [...(prev[activeTableName] ?? []).slice(-20), currentState],
      }));
    }
    setRedoHistoryByTable((prev) => ({ ...prev, [activeTableName]: [] }));
    setTransformStates((prev) => ({ ...prev, [activeTableName]: newState }));
    setIsManuallyEdited(false);
    setPage(0); // §M6: reset pagination on transform change
    setSortColumn(null); // Clear client-side sort so server ORDER BY takes precedence
    setSortDirection('asc');

    // Build SQL and update editor
    let newSql: string;
    try {
      newSql = buildSqlForTransformState(newState);
    } catch (error) {
      console.error('Failed to build SQL from transform state', error);
      return;
    }
    setSqlProgrammatically(newSql);

    // Debounced auto-run (§M8: 300ms debounce)
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      queryMutation.reset(); // §M11: clear previous errors
      const startTime = Date.now();
      queryMutation.mutate(
        { sql: newSql, limit: rowsPerPage, offset: 0, includeTotalCount: true },
        {
          onSuccess: (data) => {
            setLastSuccessfulSql(newSql);
            if (data.total_count != null) setTotalRowCount(data.total_count);
          },
          onSettled: () => setQueryTime(Date.now() - startTime),
        },
      );
    }, 300);
  }, [activeTableName, transformStates, queryMutation, rowsPerPage, buildSqlForTransformState, setSqlProgrammatically]);

  const handleUndo = useCallback(() => {
    if (stateHistory.length === 0 || !activeTableName) return;
    const prev = stateHistory[stateHistory.length - 1];
    const current = transformStates[activeTableName];
    setStateHistoryByTable((prev) => ({
      ...prev,
      [activeTableName]: (prev[activeTableName] ?? []).slice(0, -1),
    }));
    if (current) {
      setRedoHistoryByTable((prev) => ({
        ...prev,
        [activeTableName]: [...(prev[activeTableName] ?? []).slice(-20), current],
      }));
    }
    setTransformStates((s) => ({ ...s, [activeTableName]: prev }));
    setIsManuallyEdited(false);
    setPage(0);

    let newSql: string;
    try {
      newSql = buildSqlForTransformState(prev);
    } catch (error) {
      console.error('Failed to build SQL from undo state', error);
      return;
    }
    setSqlProgrammatically(newSql);
    queryMutation.reset();
    const startTime = Date.now();
    queryMutation.mutate(
      { sql: newSql, limit: rowsPerPage, offset: 0, includeTotalCount: true },
      {
        onSuccess: (data) => {
          setLastSuccessfulSql(newSql);
          if (data.total_count != null) setTotalRowCount(data.total_count);
        },
        onSettled: () => setQueryTime(Date.now() - startTime),
      },
    );
  }, [stateHistory, activeTableName, transformStates, queryMutation, rowsPerPage, buildSqlForTransformState, setSqlProgrammatically]);

  const handleRedo = useCallback(() => {
    if (redoHistory.length === 0 || !activeTableName) return;
    const next = redoHistory[redoHistory.length - 1];
    const current = transformStates[activeTableName];
    setRedoHistoryByTable((prev) => ({
      ...prev,
      [activeTableName]: (prev[activeTableName] ?? []).slice(0, -1),
    }));
    if (current) {
      setStateHistoryByTable((prev) => ({
        ...prev,
        [activeTableName]: [...(prev[activeTableName] ?? []).slice(-20), current],
      }));
    }
    setTransformStates((s) => ({ ...s, [activeTableName]: next }));
    setIsManuallyEdited(false);
    setPage(0);

    let newSql: string;
    try {
      newSql = buildSqlForTransformState(next);
    } catch (error) {
      console.error('Failed to build SQL from redo state', error);
      return;
    }
    setSqlProgrammatically(newSql);
    queryMutation.reset();
    const startTime = Date.now();
    queryMutation.mutate(
      { sql: newSql, limit: rowsPerPage, offset: 0, includeTotalCount: true },
      {
        onSuccess: (data) => {
          setLastSuccessfulSql(newSql);
          if (data.total_count != null) setTotalRowCount(data.total_count);
        },
        onSettled: () => setQueryTime(Date.now() - startTime),
      },
    );
  }, [redoHistory, activeTableName, transformStates, queryMutation, rowsPerPage, buildSqlForTransformState, setSqlProgrammatically]);

  // Default SQL when table changes (active table includes Before/After toggle targets)
  useEffect(() => {
    if (activeTableName) {
      // Use the table name as-is — it comes from the workspace API which
      // returns the actual DuckDB-registered name (e.g. "__mapped_adverse_events__").
      // Do NOT normalize: normalizeTableKey strips leading/trailing underscores
      // which breaks __mapped_*__ view names.
      const duckdbName = activeTableName;

      let tableState = transformStates[activeTableName];
      if (!tableState) {
        tableState = initStateFromTable(duckdbName);
        setTransformStates((prev) => ({ ...prev, [activeTableName]: tableState! }));
      }
      setIsManuallyEdited(false);

      let sqlToRun: string;
      try {
        sqlToRun = buildSqlForTransformState(tableState);
      } catch (error) {
        console.error('Failed to build SQL from transform state', error);
        sqlToRun = `SELECT * FROM ${quoteIdent(duckdbName)}`;
      }
      setSqlProgrammatically(sqlToRun);
      setLastSuccessfulSql('');
      setPage(0);
      setTotalRowCount(null);
      setSortColumn(null);
      setSortDirection('asc'); // §M6: reset sort on table change

      if (isTableMissing) {
        setLastSuccessfulSql('');
        return;
      }

      // Auto-run the default query
      const startTime = Date.now();
      queryMutation.mutate(
        { sql: sqlToRun, limit: rowsPerPage, offset: 0, includeTotalCount: true },
        {
          onSuccess: (data) => {
            setLastSuccessfulSql(sqlToRun);
            if (data.total_count != null) setTotalRowCount(data.total_count);
          },
          onSettled: () => setQueryTime(Date.now() - startTime),
        },
      );
    }
  }, [activeTableName]); // intentionally only re-run when table changes

  // Re-run query when dataRefreshKey changes (§M7: after Code tab save)
  useEffect(() => {
    if (dataRefreshKey && dataRefreshKey > 0 && sql.trim()) {
      queryMutation.reset();
      const startTime = Date.now();
      queryMutation.mutate(
        { sql: sql.trim(), limit: rowsPerPage, offset: page * rowsPerPage, includeTotalCount: true },
        {
          onSuccess: (data) => {
            setLastSuccessfulSql(sql.trim());
            if (data.total_count != null) setTotalRowCount(data.total_count);
          },
          onSettled: () => setQueryTime(Date.now() - startTime),
        },
      );
    }
  }, [dataRefreshKey]); // intentionally only depend on dataRefreshKey

  const handleRun = useCallback(() => {
    if (!sql.trim()) return;
    setPage(0);
    setSortColumn(null);
    setSortDirection('asc');
    const startTime = Date.now();
    queryMutation.mutate(
      { sql: sql.trim(), limit: rowsPerPage, offset: 0, includeTotalCount: true },
      {
        onSuccess: (data) => {
          setLastSuccessfulSql(sql.trim());
          if (data.total_count != null) setTotalRowCount(data.total_count);
        },
        onSettled: () => setQueryTime(Date.now() - startTime),
      },
    );
  }, [sql, queryMutation, rowsPerPage]);

  // Monaco Editor `onMount` runs once; keep Run shortcut wired to latest `handleRun`.
  const handleRunRef = useRef(handleRun);
  handleRunRef.current = handleRun;

  const handleEditorMount = useCallback((editor: any, monaco: any) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    setMonacoInstance(monaco);
    setEditorInstance(editor);
    // Fix: When the suggest widget is visible, pressing Space dismisses
    // the suggestion and types a space — instead of Monaco's default
    // behavior of accepting the suggestion (which swallows the space key
    // and sometimes inserts unwanted text). Tab/Enter still accept suggestions.
    editor.addCommand(
      monaco.KeyCode.Space,
      () => {
        editor.trigger('keyboard', 'hideSuggestWidget', {});
        editor.trigger('keyboard', 'type', { text: ' ' });
      },
      'suggestWidgetVisible',
    );
    // Safety net: macOS double-space-to-period can replace a space with ". "
    // when two space presses happen in quick succession. Detect and revert.
    let fixing = false;
    editor.onDidChangeModelContent((e: any) => {
      if (fixing) return;
      for (const change of e.changes) {
        if (change.text === '. ' && change.rangeLength >= 1) {
          fixing = true;
          const endCol = change.range.startColumn + 2;
          editor.executeEdits('fix-macos-period', [{
            range: new monaco.Range(
              change.range.startLineNumber, change.range.startColumn,
              change.range.startLineNumber, endCol,
            ),
            text: '  ',
          }]);
          fixing = false;
          break;
        }
      }
    });
    // Cmd/Ctrl+Enter to run
    editor.addAction({
      id: 'run-query',
      label: 'Run Query',
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter],
      run: () => handleRunRef.current(),
    });
  }, []);

  // Monaco IntelliSense — shared hook handles table/column completions
  useMonacoTableCompletion(monacoInstance, editorInstance, workspaceTables, 'sql', result?.columns);

  useEffect(() => {
    if (!editorInstance || !monacoInstance) return;
    const model = editorInstance.getModel?.();
    const monaco = monacoInstance;
    if (!model) return;

    const applyMarkers = () => {
      const text = model.getValue();
      const markers: Array<{
        severity: number;
        startLineNumber: number;
        startColumn: number;
        endLineNumber: number;
        endColumn: number;
        message: string;
      }> = [];
      forEachBareSqlWord(text, (word, start, end) => {
        const hint = sqlTypoKeywordSuggestion(word);
        if (!hint) return;
        const s = offsetToMonacoPos(text, start);
        const e = offsetToMonacoPos(text, end);
        markers.push({
          severity: monaco.MarkerSeverity.Error,
          startLineNumber: s.line,
          startColumn: s.col,
          endLineNumber: e.line,
          endColumn: e.col,
          message: `Unknown or misspelled SQL keyword. Did you mean "${hint}"?`,
        });
      });
      monaco.editor.setModelMarkers(model, SQL_TYPO_MARKER_OWNER, markers);
    };

    let t: ReturnType<typeof setTimeout> | undefined;
    const disposable = model.onDidChangeContent(() => {
      if (t !== undefined) clearTimeout(t);
      t = setTimeout(applyMarkers, 200);
    });
    applyMarkers();

    return () => {
      if (t !== undefined) clearTimeout(t);
      disposable.dispose();
      monaco.editor.setModelMarkers(model, SQL_TYPO_MARKER_OWNER, []);
    };
  }, [editorInstance, monacoInstance]);

  // Server returns exactly one page — use rows directly
  const paginatedRows = result?.rows ?? [];

  // ── Grid selection (cell/column/row) ──────────────────────────────
  const {
    isCellSelected, isColumnSelected, isRowSelected, hasSelection,
    handleCellMouseDown: onCellMouseDown, handleCellMouseEnter: onCellMouseEnter,
    handleColumnSelect: onColumnSelect, handleColumnMouseEnter: onColumnMouseEnter,
    handleRowSelect: onRowSelect, handleRowMouseEnter: onRowMouseEnter,
    handleSelectAll: onSelectAll,
    clearSelection, containerRef: selectionContainerRef,
  } = useGridSelection({
    columns: result?.columns ?? [],
    rows: paginatedRows,
    page,
    rowsPerPage,
    onSelectionChange,
  });

  // Clear selection when data or sort changes
  useEffect(() => { clearSelection(); }, [sortColumn, sortDirection, result]);

  // Build the effective SQL with optional ORDER BY for column-header sorting
  const buildEffectiveSql = useCallback((baseSql: string, col: string | null, dir: SortDirection) => {
    if (!col) return baseSql;
    return `SELECT * FROM (${baseSql.replace(/;$/, '')}) _sorted ORDER BY "${col.replace(/"/g, '""')}" ${dir === 'asc' ? 'ASC' : 'DESC'}`;
  }, []);

  // Server-side sort: wrap SQL with ORDER BY, reset to page 0, re-fetch
  const handleSort = useCallback((col: string) => {
    let newCol: string | null;
    let newDir: SortDirection;
    if (sortColumn === col) {
      newDir = sortDirection === 'asc' ? 'desc' : 'asc';
      newCol = col;
    } else {
      newCol = col;
      newDir = 'asc';
    }
    setSortColumn(newCol);
    setSortDirection(newDir);
    setPage(0);

    const effectiveSql = buildEffectiveSql(sql, newCol, newDir);
    const startTime = Date.now();
    queryMutation.mutate(
      { sql: effectiveSql, limit: rowsPerPage, offset: 0, includeTotalCount: false },
      { onSettled: () => setQueryTime(Date.now() - startTime) },
    );
  }, [sortColumn, sortDirection, sql, rowsPerPage, queryMutation, buildEffectiveSql]);

  const handleColumnHeaderClick = useCallback((e: React.MouseEvent<HTMLElement>, col: string) => {
    e.preventDefault();
    e.stopPropagation();
    setProfileColumn(col);
    setProfileOpen(true);
  }, []);

  // Open ColumnContextMenu on right-click (or chevron click in Phase 13)
  const handleOpenContextMenu = useCallback((e: React.MouseEvent<HTMLElement>, col: string, cellVal?: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuColumn(col);
    // If no explicit cellVal, grab first row's value for "Filter by value" pre-fill
    const rows = result?.rows ?? [];
    const firstRowVal = cellVal ?? (rows.length > 0 ? String(rows[0][col] ?? '') : undefined);
    setContextMenuCellValue(firstRowVal);
    setContextMenuAnchor(e.currentTarget);
  }, [result?.rows]);

  const handleColumnAction = useCallback((action: ColumnAction) => {
    if (!transformState || !activeTableName) return;

    // Helper to apply and propagate state
    const apply = (newState: TransformState) => handleTransformStateChange(newState);

    switch (action.type) {
      case 'profile':
        setProfileColumn(contextMenuColumn);
        setProfileOpen(true);
        break;
      case 'filter':
        apply(applyFilter(transformState, {
          column: action.column,
          operator: '=',
          value: action.value || '',
        }));
        break;
      case 'sort_asc':
        apply(applySort(transformState, action.column, 'ASC'));
        break;
      case 'sort_desc':
        apply(applySort(transformState, action.column, 'DESC'));
        break;
      case 'apply_suggestion': {
        const s = action.suggestion;
        if (s.type === 'group_by') {
          // Group By suggestions — skip for now (TransformBar handles GROUP BY)
          break;
        }
        // Map suggestion types to the correct applyColumnFunction call
        // The suggestion's sqlFragment is a full expression like COALESCE("col", '')
        // We need to extract the function name for applyColumnFunction
        switch (s.type) {
          case 'coalesce': {
            setFillNullsRequest(s.column);
            break;
          }
          case 'round': {
            const expr = s.sqlFragment;
            const existingCols = transformState.selectColumns.length === 0
              ? [{ expression: '*', alias: '' }]
              : transformState.selectColumns;
            const existingIdx = existingCols.findIndex((c) => c.alias === s.column);
            if (existingIdx >= 0) {
              const updated = [...existingCols];
              updated[existingIdx] = { expression: expr, alias: s.column, isReplacement: true };
              apply({ ...transformState, selectColumns: updated });
            } else {
              apply({ ...transformState, selectColumns: [...existingCols, { expression: expr, alias: s.column, isReplacement: true }] });
            }
            break;
          }
          case 'date_trunc': {
            const expr = s.sqlFragment;
            const cols = transformState.selectColumns.length === 0
              ? [{ expression: '*', alias: '' }]
              : transformState.selectColumns;
            apply({ ...transformState, selectColumns: [...cols, { expression: expr, alias: `${s.column}_date` }] });
            break;
          }
          default:
            // upper, trim, etc. — apply as in-place column function
            apply(applyColumnFunction(transformState, s.column, s.sqlFragment.split('(')[0]));
            break;
        }
        break;
      }
      case 'transform': {
        // Map special date functions to proper SQL
        let func = action.func;
        if (func === 'EXTRACT_YEAR') {
          const expr = `EXTRACT(year FROM ${quoteIdent(action.column)})`;
          // Addition (new column, different alias) — preserve * if needed
          const cols = transformState.selectColumns.length === 0
            ? [{ expression: '*', alias: '' }]
            : transformState.selectColumns;
          apply({ ...transformState, selectColumns: [...cols, { expression: expr, alias: `${action.column}_year` }] });
          break;
        }
        if (func === 'EXTRACT_MONTH') {
          const expr = `EXTRACT(month FROM ${quoteIdent(action.column)})`;
          const cols = transformState.selectColumns.length === 0
            ? [{ expression: '*', alias: '' }]
            : transformState.selectColumns;
          apply({ ...transformState, selectColumns: [...cols, { expression: expr, alias: `${action.column}_month` }] });
          break;
        }
        if (func === 'DATE_TRUNC_DAY') {
          const expr = `DATE_TRUNC('day', ${quoteIdent(action.column)})`;
          const cols = transformState.selectColumns.length === 0
            ? [{ expression: '*', alias: '' }]
            : transformState.selectColumns;
          apply({ ...transformState, selectColumns: [...cols, { expression: expr, alias: `${action.column}_date` }] });
          break;
        }
        if (func === 'COALESCE') {
          apply(applyCoalesce(transformState, action.column));
          break;
        }
        if (func === 'ROUND') {
          apply(applyColumnFunction(transformState, action.column, 'ROUND'));
          break;
        }
        apply(applyColumnFunction(transformState, action.column, func));
        break;
      }
      case 'rename':
        // Open rename dialog to prompt for new name
        setRenameColumn(action.column);
        setRenameNewName(action.column);
        setRenameDialogOpen(true);
        break;
      case 'drop':
        apply(applyDropColumn(transformState, action.column));
        break;
      case 'cast':
        apply(applyCast(transformState, action.column, action.targetType, columnTypeByName.get(action.column)));
        break;
      case 'ask_agent':
        if (onAskAboutColumn && activeTableName) {
          onAskAboutColumn(activeTableName, action.column);
        }
        break;
      case 'split':
        setSplitDialogColumn(action.column);
        setSplitDialogDelimiter(',');
        setSplitDialogCount(2);
        setSplitDialogOpen(true);
        break;
      case 'extract_pattern':
        setExtractPatternColumn(action.column);
        setExtractPatternRegex('');
        setExtractPatternGroupIndex('');
        setExtractPatternNewCol(`${action.column}_extracted`);
        setExtractPatternOpen(true);
        break;
      case 'move_column': {
        const allCols = result?.columns ?? [];
        if (action.position === 'first' || action.position === 'last') {
          // Apply directly — no dialog needed
          apply(applyMoveColumn(transformState, action.column, action.position, allCols));
        } else {
          // 'before' or 'after' — open dialog to pick relative column
          setMoveColumnSource(action.column);
          setMoveColumnPosition(action.position);
          setMoveColumnDialogOpen(true);
        }
        break;
      }
      case 'select_column': {
        const colIndex = result?.columns.indexOf(action.column) ?? -1;
        if (colIndex >= 0) {
          onColumnSelect(colIndex, { preventDefault: () => {}, button: 0, metaKey: false, ctrlKey: false, shiftKey: false } as unknown as React.MouseEvent);
        }
        break;
      }
    }
  }, [transformState, activeTableName, handleTransformStateChange, contextMenuColumn, onAskAboutColumn, result?.columns, onColumnSelect, columnTypeByName]);

  // ── Rename dialog handler ──────────────────────────────────────
  const renameDialogError = useMemo(() => {
    const trimmed = renameNewName.trim();
    if (!trimmed) return '';
    if (!renameColumn) return 'Select a column to rename';
    if (trimmed === renameColumn) return 'Enter a different name';
    if ((result?.columns || []).some((column) => column === trimmed && column !== renameColumn)) {
      return 'A column with this name already exists';
    }
    return '';
  }, [renameNewName, renameColumn, result?.columns]);

  const handleRenameConfirm = useCallback(() => {
    if (!transformState) {
      setRenameDialogOpen(false);
      return;
    }
    if (!renameColumn || !renameNewName.trim() || renameNewName.trim() === renameColumn || renameDialogError) return;
    handleTransformStateChange(applyRename(transformState, renameColumn, renameNewName.trim()));
    setRenameDialogOpen(false);
  }, [transformState, renameColumn, renameNewName, renameDialogError, handleTransformStateChange]);

  // ── Split dialog handler ──────────────────────────────────────
  const handleSplitConfirm = useCallback(() => {
    if (!transformState || !splitDialogColumn || !splitDialogDelimiter) {
      setSplitDialogOpen(false);
      return;
    }
    handleTransformStateChange(applySplitColumn(transformState, splitDialogColumn, splitDialogDelimiter, splitDialogCount));
    setSplitDialogOpen(false);
  }, [transformState, splitDialogColumn, splitDialogDelimiter, splitDialogCount, handleTransformStateChange]);

  // ── Extract Pattern handler ────────────────────────────────────
  const handleExtractPatternConfirm = useCallback(() => {
    if (!transformState || !extractPatternColumn || !extractPatternRegex.trim() || !extractPatternNewCol.trim()) {
      setExtractPatternOpen(false);
      return;
    }
    const groupIdx = typeof extractPatternGroupIndex === 'number' && extractPatternGroupIndex > 0
      ? extractPatternGroupIndex
      : undefined;
    handleTransformStateChange(
      applyExtractPattern(transformState, extractPatternColumn, extractPatternRegex.trim(), extractPatternNewCol.trim(), groupIdx),
    );
    setExtractPatternOpen(false);
  }, [transformState, extractPatternColumn, extractPatternRegex, extractPatternGroupIndex, extractPatternNewCol, handleTransformStateChange]);

  // Preview: apply the JS regex to the first few rows' values for the source column
  const extractPatternPreview = useMemo(() => {
    if (!extractPatternRegex.trim() || !extractPatternColumn) return [];
    const rows = result?.rows ?? [];
    const sample = rows.slice(0, 5);
    return sample.map((row) => {
      const raw = row[extractPatternColumn];
      const value = raw != null ? String(raw) : null;
      if (value == null) return { value: null, match: null };
      try {
        const re = new RegExp(extractPatternRegex);
        const m = re.exec(value);
        if (!m) return { value, match: null };
        const groupIdx = typeof extractPatternGroupIndex === 'number' && extractPatternGroupIndex > 0
          ? extractPatternGroupIndex
          : 0;
        return { value, match: m[groupIdx] ?? null };
      } catch {
        return { value, match: 'Invalid regex' };
      }
    });
  }, [extractPatternRegex, extractPatternColumn, extractPatternGroupIndex, result?.rows]);

  // ── Move Column handler ────────────────────────────────────────
  const handleMoveColumnRelative = useCallback((relativeColumn: string) => {
    if (!transformState) {
      setMoveColumnDialogOpen(false);
      return;
    }
    const allCols = result?.columns ?? [];
    handleTransformStateChange(
      applyMoveColumn(transformState, moveColumnSource, moveColumnPosition, allCols, relativeColumn),
    );
    setMoveColumnDialogOpen(false);
  }, [transformState, moveColumnSource, moveColumnPosition, result?.columns, handleTransformStateChange]);

  // ── Column drag-and-drop (header reorder) ─────────────────────
  const columnDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleColumnDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !transformState) return;
    const allCols = result?.columns ?? [];
    const activeIdx = allCols.indexOf(String(active.id));
    const overIdx = allCols.indexOf(String(over.id));
    if (activeIdx === -1 || overIdx === -1) return;
    // Moving right → place after the target; moving left → place before
    const position = activeIdx < overIdx ? 'after' : 'before';
    handleTransformStateChange(
      applyMoveColumn(transformState, String(active.id), position, allCols, String(over.id)),
    );
  }, [transformState, result?.columns, handleTransformStateChange]);

  const handleExport = useCallback(() => {
    if (!activeTableName) return;
    // When the user has applied transforms or custom SQL, export the
    // effective query results — not just the raw table.
    const effectiveSql = buildEffectiveSql(sql, sortColumn, sortDirection);
    const hasCustomSql = isManuallyEdited || (transformState && isStateModified(transformState)) || sortColumn;
    downloadWorkspaceTable(
      sessionId, activeTableName, 'csv', undefined,
      hasCustomSql ? effectiveSql : undefined,
    ).catch((err) => {
      showToast('Export failed', err?.message || 'Failed to download table data', 'error');
    });
  }, [sessionId, activeTableName, sql, sortColumn, sortDirection, isManuallyEdited, transformState, buildEffectiveSql, showToast]);

  // ── Full Validation Handler ──────────────────────────────────────
  const handleRunValidation = useCallback(async () => {
    if (!targetSchemaSnapshot || !activeTableName || !sessionId) return;
    setValidationLoading(true);
    setValidationResult(null);
    setValidationHealthIssues(undefined);
    try {
      if (mappedColumnRules.length === 0) {
        setValidationResult([]);
        return;
      }
      const validationSql = buildValidationSql(activeTableName, mappedColumnRules);
      const response = await queryWorkspace(sessionId, validationSql, 1000);
      const summaries: ValidationSummary[] = (response.rows || [])
        .filter((row) => row.violation_count > 0 || row.total_rows > 0)
        .map((row) => ({
          target_field: String(row.target_field || ''),
          rule_type: String(row.rule_type || ''),
          severity: (row.severity || 'warning') as 'error' | 'warning' | 'info',
          violation_count: Number(row.violation_count ?? 0),
          total_rows: Number(row.total_rows ?? 0),
          message: `${Number(row.violation_count ?? 0)} of ${Number(row.total_rows ?? 0)} rows violate ${row.rule_type} rule`,
        }));
      setValidationResult(summaries);
      // Convert to HealthIssue[] for column-level highlighting
      const issues: HealthIssue[] = summaries
        .filter((s) => s.violation_count > 0)
        .map((s) => ({
          column: s.target_field,
          message: s.message ?? `${s.violation_count} violations`,
          severity: s.severity,
        }));
      if (issues.length > 0) setValidationHealthIssues(issues);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Validation query failed';
      setValidationResult([{
        target_field: '',
        rule_type: 'error',
        severity: 'error',
        violation_count: 0,
        total_rows: 0,
        message: msg,
      }]);
    } finally {
      setValidationLoading(false);
    }
  }, [targetSchemaSnapshot, activeTableName, sessionId, mappedColumnRules]);

  const handleShowViolationRows = useCallback((summary: ValidationSummary) => {
    if (!activeTableName || !targetSchemaSnapshot) return;
    const columnRules = extractColumnRules(targetSchemaSnapshot, activeTableName);
    const colRule = columnRules.find((cr) => cr.columnName === summary.target_field);
    if (!colRule) return;
    // Find the matching rule
    const matchingRule = colRule.rules.find((r) => {
      const rt = (r.rule || r.type || '').toLowerCase();
      const canonical = ({ in_list: 'enum', pattern: 'regex', format: 'regex', datetime_format: 'date_format' } as Record<string, string>)[rt] || rt;
      return canonical === summary.rule_type;
    });
    // For structural rules (nullable, primary_key), build a synthetic rule
    const rule: SchemaRule = matchingRule || { rule: summary.rule_type };
    const drillSql = buildViolationRowsSql(activeTableName, summary.target_field, rule, 200);
    setSql(drillSql);
    setIsManuallyEdited(true);
    setPage(0);
    setSortColumn(null);
    setSortDirection('asc');
    // Run the drill-down query
    const startTime = Date.now();
    queryMutation.mutate(
      { sql: drillSql, limit: rowsPerPage, offset: 0, includeTotalCount: true },
      {
        onSuccess: (data) => {
          setLastSuccessfulSql(drillSql);
          if (data.total_count != null) setTotalRowCount(data.total_count);
        },
        onSettled: () => setQueryTime(Date.now() - startTime),
      },
    );
  }, [activeTableName, targetSchemaSnapshot, queryMutation, rowsPerPage]);

  // Merge prop-based healthIssues with full-validation healthIssues (used for column header badges)
  const mergedHealthIssues = useMemo(() => {
    if (!validationHealthIssues && !healthIssues) return healthIssues;
    return [...(healthIssues || []), ...(validationHealthIssues || [])];
  }, [healthIssues, validationHealthIssues]);

  // Client-side cell violations from displayed data (used for cell-level highlighting)
  const clientCellViolations = useMemo(() => {
    const rows = result?.rows ?? [];
    if (!rows.length || !targetSchemaSnapshot || !activeTableName) return [];
    if (previewTab === 'before') return [];
    if (mappedColumnRules.length === 0) return [];
    return evaluateCellViolations(rows, mappedColumnRules);
  }, [result?.rows, mappedColumnRules, previewTab]);

  // O(1) lookup map keyed by "column::rowIndex"
  const cellViolationMap = useMemo(() => {
    const map = new Map<string, CellViolation>();
    for (const v of clientCellViolations) {
      const key = `${v.column}::${v.rowIndex}`;
      const existing = map.get(key);
      if (!existing || severityRank(v.severity) > severityRank(existing.severity)) {
        map.set(key, v);
      }
    }
    return map;
  }, [clientCellViolations]);
  
  const hasValidationRules = useMemo(() => {
    if (!mappedColumnRules.length) return false;

    return mappedColumnRules.some(columnRule => {
      if (columnRule.rules && columnRule.rules.length > 0) return true;

      // Check for structural constraints that would generate validation queries
      if (columnRule.isNullable === false) return true;
      if (columnRule.isPrimaryKey === true) return true;

      return false;
    });
  }, [mappedColumnRules]);

  const addedSet = new Set(columnDiff?.added || []);
  const removedSet = new Set(columnDiff?.removed || []);

  const hasValidationPanel = !!(validationResult && validationResult.length > 0);
  const displayedRowCount = result?.rows?.length ?? 0;
  const compactResultTable = displayedRowCount > 0 && displayedRowCount <= 15;
  const resultColumnCount = result?.columns.length ?? 0;
  const resultRowsLabel = result
    ? totalRowCount != null
      ? `${result.row_count.toLocaleString()} of ${totalRowCount.toLocaleString()} rows`
      : `${result.row_count.toLocaleString()} rows`
    : '';

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Before/After toggle */}
      {inputTableName && !isSourceNode && (
        <Box sx={{ px: 1.5, py: 0.25, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={0}>
            {(['before', 'after'] as const).map((tab) => (
              <Button
                key={tab}
                size="small"
                variant={previewTab === tab ? 'contained' : 'text'}
                onClick={() => setPreviewTab(tab)}
                sx={{
                  fontSize: 11,
                  textTransform: 'capitalize',
                  py: 0.25,
                  px: 2,
                  minWidth: 0,
                  borderRadius: tab === 'before' ? '8px 0 0 8px' : '0 8px 8px 0',
                  border: '1px solid',
                  borderColor: 'divider',
                  ...(previewTab === tab
                    ? {
                        bgcolor: tab === 'after' ? 'primary.main' : 'grey.700',
                        color: '#fff',
                        '&:hover': { bgcolor: tab === 'after' ? 'primary.dark' : 'grey.800' },
                      }
                    : { color: 'text.secondary' }),
                }}
              >
                {tab === 'before'
                  ? `Before (${inputTableName || 'input'})`
                  : `After (${tableName || 'output'})`}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {/* Query bar */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Editor
              height={editorExpanded ? 160 : 40}
              language="sql"
              value={sql}
              onChange={(v) => {
                setSql(v || '');
                if (suppressSqlManualEditRef.current) {
                  suppressSqlManualEditRef.current = false;
                  return;
                }
                setIsManuallyEdited(true);
              }}
              onMount={handleEditorMount}
              wrapperProps={{
                'data-testid': 'data-explorer-sql-editor',
                'data-editor-state': 'editable',
              }}
              options={{
                ...WORKSPACE_MONACO_EDITOR_DEFAULTS,
                ariaLabel: 'Workspace data SQL query editor',
                minimap: { enabled: false },
                lineNumbers: 'on' as const,
                scrollBeyondLastLine: false,
                fontSize: 12,
                fontFamily: '"Fira Code", monospace',
                padding: { top: 4, bottom: 2 },
                renderLineHighlight: 'none' as const,
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                scrollbar: { vertical: 'hidden' as const, horizontal: 'auto' as const },
                wordWrap: 'on' as const,
                folding: false,
                glyphMargin: false,
                contextmenu: false,
                tabSize: 2,
                acceptSuggestionOnCommitCharacter: false,
              } as any}
              theme="vs-dark"
            />
          </Box>
          <Stack
            direction={editorExpanded ? 'column' : 'row'}
            sx={{ p: 0.25, gap: 0.25, bgcolor: '#1e1e1e', alignItems: 'center', flexShrink: 0 }}
          >
            <Tooltip title="Run (Cmd+Enter)">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleRun}
                  disabled={queryMutation.isLoading || !sql.trim() || isTableMissing}
                  sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' }, width: 28, height: 28 }}
                >
                  {queryMutation.isLoading ? <CircularProgress size={12} color="inherit" /> : <RunIcon sx={{ fontSize: 14 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={editorExpanded ? 'Collapse' : 'Expand'}>
              <IconButton
                size="small"
                onClick={() => {
                  setEditorExpanded((prev) => !prev);
                  window.requestAnimationFrame(() => {
                    editorInstance?.layout();
                  });
                }}
                sx={{ width: 28, height: 28, color: '#999' }}
              >
                {editorExpanded ? <CollapseIcon sx={{ fontSize: 12 }} /> : <ExpandIcon sx={{ fontSize: 12 }} />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Box>

      {/* Error */}
      {queryMutation.isError && (
        <Alert
          severity="error"
          variant="outlined"
          action={
            <IconButton
              aria-label="Dismiss error"
              color="inherit"
              size="small"
              onClick={() => queryMutation.reset()}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            mx: 1,
            mt: 0.5,
            py: 1,
            '& .MuiAlert-message': {
              fontSize: 13,
              flex: 1,
              minWidth: 0,
              maxHeight: 'min(30vh, 220px)',
              overflowY: 'auto',
              boxSizing: 'border-box',
              scrollbarGutter: 'stable',
              paddingBottom: 3,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            },
            '& .MuiAlert-action': {
              alignItems: 'flex-start',
              mr: -0.5,
              pt: 0,
            },
          }}
        >
          {getApiErrorMessage(queryMutation.error)}
        </Alert>
      )}

      {/* Table Missing Warning */}
      {isTableMissing && (
        <Alert
          severity="warning"
          sx={{
            mx: 1,
            mt: 0.5,
            py: 1,
            '& .MuiAlert-message': {
              fontSize: 13,
            },
          }}
        >
          Source table "{activeTableName}" not found. It may have been deleted or the pipeline state is stale.
        </Alert>
      )}

      {/* TransformBar — point-and-click SQL operations */}
      {activeTableName && transformState && (
        <TransformBar
          transformState={transformState}
          onStateChange={handleTransformStateChange}
          columns={result?.columns || []}
          columnTypes={columnTypes}
          columnNullStats={columnNullStats}
          workspaceTables={workspaceTables}
          disabled={queryMutation.isLoading}
          canUndo={!isManuallyEdited && stateHistory.length > 0}
          onUndo={handleUndo}
          canRedo={!isManuallyEdited && redoHistory.length > 0}
          onRedo={handleRedo}
          sessionId={sessionId}
          fillNullsRequest={fillNullsRequest}
          onFillNullsRequestHandled={() => setFillNullsRequest(null)}
        />
      )}

      {/* Toolbar */}
      {result && (
        <Box sx={{ px: 1.5, py: 0.25, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Chip
            label={`${resultRowsLabel} · ${resultColumnCount} cols`}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11, height: 22 }}
          />
          {queryTime != null && (
            <Typography variant="caption" color="text.secondary">
              {formatElapsed(queryTime)}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {/* Quality dot legend — show when any column has nulls */}
          {Object.values(columnNullStats).some((ns) => ns.pct > 0) && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mr: 1 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 10 }}>Nulls:</Typography>
              {([
                { color: '#ef4444', label: '>50%' },
                { color: '#f59e0b', label: '10–50%' },
                { color: '#22c55e', label: '<10%' },
              ] as const).map(({ color, label }) => (
                <Stack key={color} direction="row" spacing={0.25} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>{label}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
          {/* Violation legend */}
          {mergedHealthIssues && mergedHealthIssues.length > 0 && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mr: 1 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 10 }}>Issues:</Typography>
              {mergedHealthIssues.some(h => h.severity === 'error') && (
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: 0.5, bgcolor: '#ef4444', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>Error</Typography>
                </Stack>
              )}
              {mergedHealthIssues.some(h => h.severity === 'warning') && (
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: 0.5, bgcolor: '#f59e0b', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontSize: 10, color: '#94a3b8' }}>Warning</Typography>
                </Stack>
              )}
            </Stack>
          )}
          {/* Full Validation button */}
          {targetSchemaSnapshot && activeTableName && (
              <Tooltip title={hasValidationRules ? "Validate all rows against schema rules" : "No validation rules defined for this schema"}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={validationLoading ? <CircularProgress size={12} color="inherit" /> : <FactCheckIcon sx={{ fontSize: 14 }} />}
                    disabled={validationLoading || !hasValidationRules}
                    onClick={handleRunValidation}
                    sx={{
                      fontSize: 11,
                      textTransform: 'none',
                      height: 24,
                      px: 1.5,
                      borderColor: validationResult && validationResult.some(v => v.violation_count > 0)
                        ? '#ef4444'
                        : undefined,
                      color: validationResult && validationResult.some(v => v.violation_count > 0)
                        ? '#ef4444'
                        : undefined,
                    }}
                  >
                    {validationLoading ? 'Validating…' : 'Validate'}
                  </Button>
                </span>
            </Tooltip>
          )}
          <Tooltip title="Export as CSV">
            <IconButton size="small" onClick={handleExport} sx={{ p: 0.25 }}>
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Validation + results — share remaining height; compact tables don't stretch */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {hasValidationPanel && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: alpha('#f8fafc', 0.8),
            flex: compactResultTable ? 1 : '0 1 auto',
            minHeight: 0,
            maxHeight: compactResultTable ? undefined : 'min(35vh, 280px)',
            overflowY: 'auto',
            scrollbarGutter: 'stable',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
            <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11 }}>
              Full Validation: {validationResult!.filter(v => v.violation_count === 0).length} rules passed
              {validationResult!.some(v => v.violation_count > 0) && (
                <Typography component="span" variant="caption" sx={{ color: '#ef4444', fontWeight: 700, fontSize: 11 }}>
                  {' · '}{validationResult!.filter(v => v.violation_count > 0).length} with violations
                </Typography>
              )}
            </Typography>
            <IconButton
              size="small"
              onClick={() => { setValidationResult(null); setValidationHealthIssues(undefined); }}
              sx={{ p: 0.25 }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
          <Stack spacing={0.25}>
            {/* Show failing rules first, then passing */}
            {validationResult!
              .sort((a, b) => b.violation_count - a.violation_count)
              .map((v, idx) => {
                const hasFail = v.violation_count > 0;
                const SevIcon = hasFail
                  ? v.severity === 'error' ? FailErrorIcon : FailWarningIcon
                  : PassIcon;
                const color = hasFail
                  ? v.severity === 'error' ? '#ef4444' : '#f59e0b'
                  : '#22c55e';
                return (
                  <Stack key={idx} direction="row" alignItems="center" spacing={0.75} sx={{ py: 0.25 }}>
                    <SevIcon sx={{ fontSize: 14, color }} />
                    <Typography variant="caption" sx={{ fontSize: 11, fontFamily: '"Fira Code", monospace', minWidth: 120 }}>
                      {v.target_field}
                    </Typography>
                    <Chip
                      label={v.rule_type}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: 9,
                        fontWeight: 600,
                        bgcolor: alpha(color, 0.1),
                        color,
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, flex: 1 }}>
                      {hasFail
                        ? `${v.violation_count.toLocaleString()} of ${v.total_rows.toLocaleString()} rows`
                        : '0 violations'}
                    </Typography>
                    {hasFail && (
                      <Button
                        size="small"
                        onClick={() => handleShowViolationRows(v)}
                        sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, py: 0, px: 0.75, height: 18 }}
                      >
                        Show rows
                      </Button>
                    )}
                  </Stack>
                );
              })}
          </Stack>
        </Box>
      )}

      {/* Results table */}
      <Box
        ref={selectionContainerRef}
        tabIndex={0}
        sx={{
          flex: hasValidationPanel ? (compactResultTable ? '0 0 auto' : 1) : 1,
          minHeight: hasValidationPanel && !compactResultTable ? 120 : 0,
          overflow: 'hidden',
          outline: 'none',
          ...(hasSelection && { userSelect: 'none' }),
        }}
      >
        {queryMutation.isLoading && !result && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {result && result.columns.length > 0 && (
          // DndContext must live outside <table> — the dnd-kit accessibility layer injects a
          // live-region <div> which is invalid inside <table>/<thead>/<tr>.
          // The `accessibility` prop moves that announcer into a portal outside the DOM tree.
          <DndContext
            sensors={columnDragSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleColumnDragEnd}
            modifiers={[restrictToHorizontalAxis]}
            accessibility={{ container: document.body }}
          >
          <TableContainer sx={{ maxHeight: compactResultTable && hasValidationPanel ? undefined : '100%', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={onSelectAll}
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: 'text.secondary',
                      bgcolor: '#f8fafc',
                      borderBottom: '2px solid #e2e8f0',
                      width: 48,
                      py: 0.75,
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#eef2ff' },
                    }}
                  >
                    #
                  </TableCell>
                  <SortableContext
                    items={result.columns}
                    strategy={horizontalListSortingStrategy}
                  >
                  {result.columns.map((col, colIdx) => {
                    const colIssues = mergedHealthIssues?.filter(h => h.column === col) || [];
                    const colHasErrors = colIssues.some(h => h.severity === 'error');
                    const colHasWarnings = colIssues.length > 0;
                    return (
                    <SortableHeaderCell
                      id={col}
                      key={col}
                      onMouseDown={(e) => {
                        // Shift+click selects the column; plain click falls through to sort label
                        if (e.shiftKey) {
                          onColumnSelect(colIdx, e);
                        }
                      }}
                      onMouseEnter={() => onColumnMouseEnter(colIdx)}
                      sx={{
                        fontFamily: '"Fira Code", monospace',
                        fontWeight: 700,
                        fontSize: 11,
                        whiteSpace: 'nowrap',
                        zIndex: 2,
                        bgcolor:
                          isColumnSelected(colIdx)
                            ? '#d4e3fc'
                            : colHasErrors
                            ? '#fef1f1'
                            : colHasWarnings
                            ? '#fef8ec'
                            : previewTab === 'after' && addedSet.has(col)
                            ? '#dcfce7'
                            : previewTab === 'before' && removedSet.has(col)
                            ? '#fee2e2'
                            : '#f8fafc',
                        color:
                          previewTab === 'after' && addedSet.has(col)
                            ? '#166534'
                            : previewTab === 'before' && removedSet.has(col)
                            ? '#991b1b'
                            : 'text.primary',
                        borderBottom: isColumnSelected(colIdx)
                          ? '2px solid #3b82f6'
                          : colHasErrors
                          ? '2px solid #ef4444'
                          : colHasWarnings
                          ? '2px solid #f59e0b'
                          : '2px solid #e2e8f0',
                        py: 0.75,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.25}>
                        {/* Type icon */}
                        {getTypeIcon(columnTypeByName.get(col) || '')}
                        <TableSortLabel
                          active={sortColumn === col}
                          direction={sortColumn === col ? sortDirection : 'asc'}
                          onClick={() => handleSort(col)}
                          sx={{ '& .MuiTableSortLabel-icon': { fontSize: 14 } }}
                        >
                          <span
                            onContextMenu={(e) => handleOpenContextMenu(e, col)}
                            onClick={(e) => {
                              // Ctrl+click opens profile popover (existing behavior)
                              if (e.ctrlKey || e.metaKey) {
                                handleColumnHeaderClick(e, col);
                              }
                            }}
                          >
                            {col}
                          </span>
                        </TableSortLabel>
                        {/* Dropdown chevron for context menu */}
                        <ChevronIcon
                          sx={{
                            fontSize: 14,
                            color: 'text.disabled',
                            cursor: 'pointer',
                            '&:hover': { color: 'text.primary' },
                            ml: -0.25,
                          }}
                          onClick={(e) => handleOpenContextMenu(e as any, col)}
                        />
                        {/* Quality indicator dot */}
                        {(() => {
                          const ns = columnNullStats[col];
                          if (!ns || ns.pct === 0) return null;
                          const color = ns.pct > 0.5 ? '#ef4444' : ns.pct > 0.1 ? '#f59e0b' : '#22c55e';
                          return (
                            <Tooltip title={`${(ns.pct * 100).toFixed(1)}% null (${ns.nullCount} of ${ns.total})`}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                            </Tooltip>
                          );
                        })()}
                        {/* Violation badge from health issues */}
                        {(() => {
                          if (!mergedHealthIssues) return null;
                          const colIssues = mergedHealthIssues.filter((h) => h.column === col);
                          if (colIssues.length === 0) return null;
                          const errors = colIssues.filter((h) => h.severity === 'error').length;
                          const color = errors > 0 ? '#ef4444' : '#f59e0b';
                          return (
                            <Tooltip title={colIssues.map((h) => h.message).join('\n')}>
                              <Chip
                                label={colIssues.length}
                                size="small"
                                sx={{
                                  height: 16,
                                  fontSize: 9,
                                  fontWeight: 700,
                                  bgcolor: alpha(color, 0.15),
                                  color,
                                  '& .MuiChip-label': { px: 0.5 },
                                }}
                              />
                            </Tooltip>
                          );
                        })()}
                      </Stack>
                      {/* Quality bar below column name */}
                      {columnQuality?.[col] && (
                        <Tooltip
                          title={`${columnQuality[col].validPct.toFixed(1)}% valid · ${columnQuality[col].nullPct.toFixed(1)}% null · ${columnQuality[col].violationCount} violations`}
                        >
                          <Box sx={{ display: 'flex', height: 3, borderRadius: 1, mt: 0.25, overflow: 'hidden', bgcolor: '#e2e8f0' }}>
                            <Box sx={{ width: `${columnQuality[col].validPct}%`, bgcolor: '#22c55e' }} />
                            <Box sx={{ width: `${columnQuality[col].nullPct}%`, bgcolor: '#ef4444' }} />
                          </Box>
                        </Tooltip>
                      )}
                    </SortableHeaderCell>
                    );
                  })}
                  </SortableContext>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((row, i) => {
                  const absoluteRowIdx = page * rowsPerPage + i;
                  // Violation map uses local row index (0-based within current page)
                  // since evaluateCellViolations indexes into result.rows directly.
                  const violationRowIdx = i;
                  // Row-level severity: check if any cell in this row has a violation
                  const rowIssue = result.columns.reduce<CellViolation | undefined>((worst, col) => {
                    const v = cellViolationMap.get(`${col}::${violationRowIdx}`);
                    if (!v) return worst;
                    if (!worst || severityRank(v.severity) > severityRank(worst.severity)) return v;
                    return worst;
                  }, undefined);
                  return (
                  <TableRow key={absoluteRowIdx} hover sx={isRowSelected(i) ? { bgcolor: alpha('#3b82f6', 0.12) } : undefined}>
                    <TableCell
                      onMouseDown={(e) => onRowSelect(i, e)}
                      onMouseEnter={() => onRowMouseEnter(i)}
                      sx={{
                        fontSize: 10,
                        color: isRowSelected(i) ? '#3b82f6' : rowIssue ? (rowIssue.severity === 'error' ? '#ef4444' : '#f59e0b') : 'text.disabled',
                        fontWeight: isRowSelected(i) || rowIssue ? 700 : 400,
                        py: 0.5,
                        position: 'sticky',
                        left: 0,
                        bgcolor: isRowSelected(i)
                          ? alpha('#3b82f6', 0.25)
                          : rowIssue
                          ? alpha(rowIssue.severity === 'error' ? '#ef4444' : '#f59e0b', 0.12)
                          : '#fff',
                        zIndex: 1,
                        borderLeft: rowIssue
                          ? `3px solid ${rowIssue.severity === 'error' ? '#ef4444' : '#f59e0b'}`
                          : undefined,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: alpha('#3b82f6', 0.18) },
                      }}
                    >
                      {absoluteRowIdx + 1}
                    </TableCell>
                    {result.columns.map((col, colIdx) => {
                      const cellIssue = cellViolationMap.get(`${col}::${violationRowIdx}`);
                      const cellSelected = isCellSelected(i, colIdx);
                      const columnType = columnTypeByName.get(col);
                      const displayValue = row[col] != null
                        ? formatCellDisplayValue(row[col], columnType, selectExpressionByColumn.get(col))
                        : '';
                      const cellBgColor = cellSelected
                        ? alpha('#3b82f6', 0.22)
                        : cellIssue
                        ? cellIssue.severity === 'error'
                          ? alpha('#ef4444', 0.1)
                          : alpha('#f59e0b', 0.1)
                        : previewTab === 'after' && addedSet.has(col)
                        ? alpha('#dcfce7', 0.4)
                        : previewTab === 'before' && removedSet.has(col)
                        ? alpha('#fee2e2', 0.4)
                        : 'transparent';
                      const cellContent = (
                        <TableCell
                          key={col}
                          onMouseDown={(e) => onCellMouseDown(i, colIdx, e)}
                          onMouseEnter={() => onCellMouseEnter(i, colIdx)}
                          sx={{
                            fontFamily: '"Fira Code", monospace',
                            fontSize: 11,
                            whiteSpace: 'nowrap',
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            py: 0.5,
                            cursor: 'cell',
                            bgcolor: cellBgColor,
                            boxShadow: cellSelected && cellIssue
                              ? 'inset 0 0 0 1.5px rgba(59, 130, 246, 0.4)'
                              : undefined,
                            borderLeft: cellIssue
                              ? `3px solid ${cellIssue.severity === 'error' ? '#ef4444' : '#f59e0b'}`
                              : undefined,
                          }}
                        >
                          {row[col] != null ? (
                            displayValue
                          ) : (
                            <Typography component="span" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 11 }}>
                              null
                            </Typography>
                          )}
                        </TableCell>
                      );
                      return cellIssue ? (
                        <Tooltip
                          key={col}
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>
                                {cellIssue.message}
                              </Typography>
                              {cellIssue.remediation && (
                                <Typography variant="caption" sx={{ display: 'block', mt: 0.25, opacity: 0.85 }}>
                                  {cellIssue.remediation}
                                </Typography>
                              )}
                            </Box>
                          }
                          placement="top"
                          arrow
                        >
                          {cellContent}
                        </Tooltip>
                      ) : (
                        <Tooltip key={col} title={displayValue} placement="top">
                          {cellContent}
                        </Tooltip>
                      );
                    })}
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </DndContext>
        )}
        {result && result.columns.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body2" color="text.disabled">
              Query returned no columns
            </Typography>
          </Box>
        )}
        {!result && !queryMutation.isLoading && !queryMutation.isError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 1, py: 4 }}>
            {errorMessage ? (
              <>
                <Typography variant="body2" color="error" fontWeight={600}>
                  Materialization Failed
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 480, textAlign: 'center' }}>
                  {errorMessage}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.disabled">
                {activeTableName 
                  ? (activeTableName.startsWith('__mapped_') 
                      ? 'No mapping data found for this table yet' 
                      : 'Press Run to query data')
                  : 'No table selected'}
              </Typography>
            )}
          </Box>
        )}
      </Box>
      </Box>

      {/* SaveTransformBar — appears when SQL modified via toolbar */}
      {activeTableName && transformState && (
        <SaveTransformBar
          sessionId={sessionId}
          currentSql={sql}
          transformState={transformState}
          isModified={isStateModified(transformState) || isManuallyEdited}
          operationContext={operationContext}
          workspaceTables={workspaceTables}
          onOperationCreated={onOperationCreated}
          onOperationUpdated={onOperationUpdated}
        />
      )}

      {/* Pagination */}
      {result && (totalRowCount ?? result.row_count) > 0 && (
        <TablePagination
          component="div"
          count={totalRowCount ?? result.row_count}
          page={page}
          onPageChange={(_, newPage) => {
            setPage(newPage);
            const offset = newPage * rowsPerPage;
            const effectiveSql = buildEffectiveSql(sql, sortColumn, sortDirection);
            const startTime = Date.now();
            queryMutation.mutate(
              { sql: effectiveSql, limit: rowsPerPage, offset, includeTotalCount: false },
              { onSettled: () => setQueryTime(Date.now() - startTime) },
            );
          }}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const newRowsPerPage = parseInt(e.target.value, 10);
            setRowsPerPage(newRowsPerPage);
            setPage(0);
            const effectiveSql = buildEffectiveSql(sql, sortColumn, sortDirection);
            const startTime = Date.now();
            queryMutation.mutate(
              { sql: effectiveSql, limit: newRowsPerPage, offset: 0, includeTotalCount: true },
              {
                onSuccess: (data) => {
                  setLastSuccessfulSql(effectiveSql);
                  if (data.total_count != null) setTotalRowCount(data.total_count);
                },
                onSettled: () => setQueryTime(Date.now() - startTime),
              },
            );
          }}
          rowsPerPageOptions={[50, 100, 200]}
          nextIconButtonProps={{ disabled: queryMutation.isLoading || (page + 1) * rowsPerPage >= (totalRowCount ?? result.row_count) }}
          backIconButtonProps={{ disabled: queryMutation.isLoading || page === 0 }}
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': { minHeight: 36 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 11 },
          }}
        />
      )}

      {/* Column profile panel */}
      {activeTableName && profileColumn && (
        <ColumnProfilePanel
          sessionId={sessionId}
          tableName={activeTableName}
          columnName={profileColumn}
          displayDtype={columnTypeByName.get(profileColumn)}
          querySql={resolveProfileQuerySql(profileColumn)}
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
        />
      )}

      {/* Column context menu */}
      {activeTableName && contextMenuColumn && (
        <ColumnContextMenu
          anchorEl={contextMenuAnchor}
          onClose={() => setContextMenuAnchor(null)}
          columnName={contextMenuColumn}
          columnType={columnTypeByName.get(contextMenuColumn) || 'VARCHAR'}
          cellValue={contextMenuCellValue}
          profile={contextMenuProfile}
          profileLoading={contextMenuProfileLoading}
          onAction={handleColumnAction}
          allColumns={result?.columns ?? []}
        />
      )}

      {/* Extract Pattern dialog */}
      <Dialog
        open={extractPatternOpen}
        onClose={() => setExtractPatternOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, pb: 1 }}>
          Extract Pattern — {extractPatternColumn}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Regex pattern"
              placeholder="e.g. ([A-Z]\d+\.?\d*)"
              value={extractPatternRegex}
              onChange={(e) => setExtractPatternRegex(e.target.value)}
              sx={{ '& input': { fontFamily: '"Fira Code", monospace', fontSize: 13 } }}
              helperText="Use capture groups ( ) to extract a specific part. DuckDB uses RE2 syntax."
            />
            <TextField
              fullWidth
              size="small"
              label="Capture group index (optional)"
              placeholder="e.g. 1 for first group; leave blank to match full pattern"
              type="number"
              value={extractPatternGroupIndex}
              onChange={(e) => setExtractPatternGroupIndex(e.target.value === '' ? '' : Math.max(1, Number(e.target.value)))}
              inputProps={{ min: 1 }}
              sx={{ '& input': { fontSize: 13 } }}
            />
            <TextField
              fullWidth
              size="small"
              label="New column name"
              value={extractPatternNewCol}
              onChange={(e) => setExtractPatternNewCol(e.target.value)}
              sx={{ '& input': { fontFamily: '"Fira Code", monospace', fontSize: 13 } }}
            />
            {/* Preview */}
            {extractPatternPreview.length > 0 && extractPatternRegex.trim() && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Preview (first {extractPatternPreview.length} rows)
                </Typography>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  {extractPatternPreview.map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        borderBottom: i < extractPatternPreview.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: '"Fira Code", monospace', color: 'text.secondary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {item.value ?? 'null'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>→</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: '"Fira Code", monospace',
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: item.match === null ? '#94a3b8' : item.match === 'Invalid regex' ? 'error.main' : 'success.main',
                          fontStyle: item.match === null ? 'italic' : 'normal',
                        }}
                      >
                        {item.match === null ? 'NULL' : item.match}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setExtractPatternOpen(false)}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleExtractPatternConfirm}
            disabled={!extractPatternRegex.trim() || !extractPatternNewCol.trim()}
          >
            Extract
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Column — relative position picker (before / after) */}
      <Dialog
        open={moveColumnDialogOpen}
        onClose={() => setMoveColumnDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, pb: 1 }}>
          Move "{moveColumnSource}" {moveColumnPosition === 'before' ? 'before' : 'after'}…
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Select the target column:
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {(result?.columns ?? [])
              .filter((col) => col !== moveColumnSource)
              .map((col, i, arr) => (
                <MenuItem
                  key={col}
                  onClick={() => handleMoveColumnRelative(col)}
                  sx={{
                    fontSize: 12,
                    fontFamily: '"Fira Code", monospace',
                    borderBottom: i < arr.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    py: 0.75,
                  }}
                >
                  {col}
                </MenuItem>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setMoveColumnDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Rename dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, pb: 1 }}>Rename Column</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="New column name"
            value={renameNewName}
            onChange={(e) => setRenameNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameConfirm(); }}
            error={!!renameDialogError}
            helperText={renameDialogError || ' '}
            sx={{ mt: 1, '& input': { fontFamily: '"Fira Code", monospace', fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleRenameConfirm}
            disabled={!renameNewName.trim() || renameNewName.trim() === renameColumn || !!renameDialogError}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Split dialog */}
      <Dialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, pb: 1 }}>Split Column: {splitDialogColumn}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Delimiter"
              value={splitDialogDelimiter}
              onChange={(e) => setSplitDialogDelimiter(e.target.value)}
              sx={{ '& input': { fontFamily: '"Fira Code", monospace', fontSize: 13 } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Number of output columns"
              type="number"
              value={splitDialogCount}
              onChange={(e) => setSplitDialogCount(Math.max(2, Math.min(10, Number(e.target.value) || 2)))}
              inputProps={{ min: 2, max: 10 }}
              sx={{ '& input': { fontSize: 13 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSplitDialogOpen(false)}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSplitConfirm}
            disabled={!splitDialogDelimiter}
          >
            Split
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataExplorerTab;
